
import React, {
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import { connect } from "react-redux";
import {
  Box,
  Button,
  TextField,
  Typography,
} from "@mui/material";

import { Table } from "../../layout";
import * as actions from "../../api/actions/pos";
import { AppContext } from "../../App";
import { controller, endpoints } from "../../api/actions/api";
import { safeOnComplete } from "../../utils/safeOnComplete";
import { formatCurrency } from "../../hooks/Currency";
import ReportPrintButton from "./ReportPrintButton";

const EmployeeReport = (props) => {
  const { t } = useTranslation();
  const { _store, _getById } = props;
  const { setLoader } = useContext(AppContext);

  const reportRef = useRef(null);

  const [sells, setSells] = useState([]);
  const [voidSells, setVoidSells] = useState([]);
  const [cancelItems, setCancelItems] = useState([]);

  // Period filters
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");

  const invalidRange = Boolean(
    fromDate && toDate && fromDate > toDate
  );

  useEffect(() => {
    setSells([]);
    setVoidSells([]);
    setCancelItems([]);

    if (!_store?.id) return;

    setLoader(true);

    // Sales
    _getById(
      endpoints.Sell + controller.GetSellsByStore,
      _store.id,
      null,
      (res, err) => {
        safeOnComplete(
          (response) => {
            setSells(
              Array.isArray(response.result)
                ? response.result
                : []
            );
          },
          (error) => {
            console.error("Failed to load sells:", error);
            setSells([]);
          }
        )(res, err);

        setLoader(false);
      }
    );

    // Voids and refunds
    _getById(
      endpoints.VoidSell + controller.GetByStore,
      _store.id,
      null,
      (res) => {
        setVoidSells(
          res?.status === 200 && Array.isArray(res.result)
            ? res.result
            : []
        );
      }
    );

    // Cancelled items
    _getById(
      endpoints.Sell + controller.GetCancelCartsByStore,
      _store.id,
      null,
      (res) => {
        setCancelItems(
          res?.status === 200 && Array.isArray(res.result)
            ? res.result
            : []
        );
      }
    );

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [_store?.id]);

  // Inclusive date range filter
  const isWithinPeriod = (dateValue) => {
    if (!fromDate && !toDate) return true;

    if (!dateValue) return false;

    const date = new Date(dateValue);

    if (Number.isNaN(date.getTime())) return false;

    if (fromDate) {
      const start = new Date(`${fromDate}T00:00:00`);

      if (date < start) return false;
    }

    if (toDate) {
      const end = new Date(`${toDate}T23:59:59.999`);

      if (date > end) return false;
    }

    return true;
  };

  // Filter datasets before calculating totals
  const reportRows = useMemo(() => {
    if (invalidRange) return [];

    const filteredSells = sells.filter((sale) =>
      isWithinPeriod(sale.sellDate)
    );

    // Preserve existing API date fields
    const filteredVoids = voidSells.filter((item) =>
      isWithinPeriod(item.addDate)
    );

    const filteredCancels = cancelItems.filter((item) =>
      isWithinPeriod(item.addDate)
    );

    const employees = new Map();

    // Group sales by employee
    filteredSells.forEach((sale) => {
      const userId = sale.userId;

      if (userId == null) return;

      if (!employees.has(userId)) {
        employees.set(userId, {
          userId,
          emplName: [
            sale.user?.firstName,
            sale.user?.lastName,
          ]
            .filter(Boolean)
            .join(" "),
          totalSale: 0,
          voidSale: 0,
          refund: 0,
          itemCancel: 0,
        });
      }

      const employee = employees.get(userId);

      (sale.sellItems || []).forEach((item) => {
        employee.totalSale +=
          Number(item.qty || 0) *
          Number(item.price || 0);
      });
    });

    // Voids and refunds
    filteredVoids.forEach((item) => {
      const employee = employees.get(item.userId);

      if (!employee) return;

      const amount = Number(item.subtotal || 0);

      // Preserve original calculation:
      // voidSale includes all void/refund records.
      employee.voidSale += amount;

      if (item.cancelType === 2) {
        employee.refund += amount;
      }
    });

    // Cancelled items
    filteredCancels.forEach((item) => {
      const employee = employees.get(item.userId);

      if (employee) {
        employee.itemCancel += 1;
      }
    });

    return Array.from(employees.values()).map((employee) => ({
      emplName: employee.emplName,

      totalSale: formatCurrency(
        employee.totalSale,
        _store
      ),

      voidSale: formatCurrency(
        employee.voidSale,
        _store
      ),

      refund: formatCurrency(
        employee.refund,
        _store
      ),

      itemCancel: employee.itemCancel,
    }));

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    sells,
    voidSells,
    cancelItems,
    fromDate,
    toDate,
    _store,
    invalidRange,
  ]);

  // Shared table columns
  const columns = [
    {
      id: "emplName",
      label: t("employeeName"),
    },
    {
      id: "totalSale",
      label: t("totalSale"),
    },
    {
      id: "voidSale",
      label: t("voidSale"),
    },
    {
      id: "refund",
      label: t("refund"),
    },
    {
      id: "itemCancel",
      label: t("itemCancel"),
    },
  ];

  return (
    <>
      {/* Date filters and Print Preview button */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          flexWrap: "wrap",
          gap: 2,
          mb: 3,
        }}
      >
        <Typography variant="h6" sx={{ mr: 1 }}>
          {t("employeeReport")}
        </Typography>

        <TextField
          label={t("fromDate")}
          type="date"
          size="small"
          value={fromDate}
          onChange={(e) =>
            setFromDate(e.target.value)
          }
          InputLabelProps={{ shrink: true }}
        />

        <TextField
          label={t("toDate")}
          type="date"
          size="small"
          value={toDate}
          onChange={(e) =>
            setToDate(e.target.value)
          }
          inputProps={{
            min: fromDate || undefined,
          }}
          InputLabelProps={{ shrink: true }}
          error={invalidRange}
          helperText={
            invalidRange
              ? "To Date must be on or after From Date"
              : ""
          }
        />

        <Button
          variant="outlined"
          onClick={() => {
            setFromDate("");
            setToDate("");
          }}
        >
          {t("clear")}
        </Button>

        <ReportPrintButton
          reportRef={reportRef}
          paperSize="Letter"
          reportTitle={t("employeeReport")}
        />
      </Box>

      {/* Normal screen report */}
      {/* Search and Pagination remain enabled */}
      <Box
        sx={{
          backgroundColor: "#fff",
          color: "#000",
          p: 2,
        }}
      >
        <Typography
          variant="h6"
          sx={{
            mb: 1,
            fontWeight: "bold",
            textAlign: "center",
          }}
        >
          {t("employeeReport")}
        </Typography>

        {(fromDate || toDate) && (
          <Typography
            variant="body2"
            sx={{
              mb: 2,
              textAlign: "center",
            }}
          >
            {t("fromDate")}: {fromDate || "All"}
            {"  —  "}
            {t("toDate")}: {toDate || "All"}
          </Typography>
        )}

        <Table
          ActionComp={() => <div />}
          HeaderComp={() => <div />}
          tblHead={columns}
          tblData={reportRows}
        />
      </Box>

      {/* Separate printable report - all rows */}
      <Box
        ref={reportRef}
        className="employee-report-print"
        sx={{
          position: "absolute",
          left: "-10000px",
          top: 0,
          width: "1000px",
          backgroundColor: "#fff",
          color: "#000",
          p: 2,
        }}
      >
        <Typography
          variant="h6"
          sx={{
            mb: 1,
            fontWeight: "bold",
            textAlign: "center",
          }}
        >
          {t("employeeReport")}
        </Typography>

        {(fromDate || toDate) && (
          <Typography
            variant="body2"
            sx={{
              mb: 2,
              textAlign: "center",
            }}
          >
            {t("fromDate")}: {fromDate || "All"}
            {"  —  "}
            {t("toDate")}: {toDate || "All"}
          </Typography>
        )}

        {/* All records without Search or Pagination */}
        <Table
          printMode={true}
          ActionComp={() => <div />}
          HeaderComp={() => <div />}
          tblHead={columns}
          tblData={reportRows}
        />
      </Box>
    </>
  );
};

const mapStateToProps = (state) => ({
  _store: state.pos.defStore,
});

const mapActionToProps = {
  _getById: actions.getById,
};

export default connect(
  mapStateToProps,
  mapActionToProps
)(EmployeeReport);
